/**
 * ELFÉR-E A SOROZAT EGY KÉRÉSBEN — a többoldalas küldés kemény korlátja.
 *
 * ⚠️ EGY SOROZAT NEM DARABOLHATÓ. A `useUploader` a kötegeket méret szerint
 * szétvágja, és az az EGYFÁJLOS úton helyes: minden fájlból külön bizonylat
 * lesz, tehát mindegy, melyik kérésben megy. Egy oldalsorozatnál viszont a
 * darabolás CSENDBEN elrontaná a bizonylatot:
 *
 * - a második darab a szerveren a folytatás-ágra futna,
 * - ahol a teljes-oldalkészlet-őr elutasítaná (`document-ingest.service.ts`),
 * - és az ügyfél egy fél sorozat után kapna hibát, a papírral a kezében.
 *
 * Ezért a korlátot ELŐRE mérjük, a kamerában, amíg a lap még ott van — és ha
 * nem fér bele, azt MOST mondjuk meg, nem a küldés közepén.
 */

import { MAX_FILES_PER_UPLOAD, MAX_UPLOAD_BYTES } from '@/contracts/generated/upload-limits';

export type SessionLimit = 'ok' | 'too-many-pages' | 'too-large';

/**
 * Belefér-e még EGY oldal a sorozatba.
 *
 * A KÖVETKEZŐ lapot méri, nem a meglévőket: a kamerának azelőtt kell szólnia,
 * hogy az ügyfél lefotózná a huszonegyediket. Egy utólagos „ez már nem fér
 * bele" a munkáját dobná el.
 */
export function canAddPage(pages: readonly Blob[], nextBytes: number): SessionLimit {
  if (pages.length >= MAX_FILES_PER_UPLOAD) {
    return 'too-many-pages';
  }

  const used = pages.reduce((sum, page) => sum + page.size, 0);
  if (used + nextBytes > MAX_UPLOAD_BYTES) {
    return 'too-large';
  }

  return 'ok';
}

/**
 * Egy MÁR KÉSZ sorozat elfér-e. A küldés gombja ezt nézi.
 *
 * Elvileg a `canAddPage` miatt sosem lehet `ok`-tól eltérő — de a két
 * ellenőrzés más pillanatban fut, és a lapok mérete a kivágás után derül ki.
 * Egy küldés előtti második mérés olcsóbb, mint egy fél sorozat után kapott
 * szerverhiba.
 */
export function fitsInOneRequest(pages: readonly Blob[]): SessionLimit {
  if (pages.length > MAX_FILES_PER_UPLOAD) {
    return 'too-many-pages';
  }
  const total = pages.reduce((sum, page) => sum + page.size, 0);
  return total > MAX_UPLOAD_BYTES ? 'too-large' : 'ok';
}
